import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import ListActions from 'Components/ListActions'
import ListSearch from 'Components/ListSearch'
import { WithLoader } from 'Components/WithLoader'
import SalesPointDrawer from 'Services/ServiceProviderManagement/Features/SalesPoint/Components/SalesPointDrawer'
import SalesPointList from 'Services/ServiceProviderManagement/Features/SalesPoint/Components/SalesPointList'
import useSalesPointsWithParams from 'Services/ServiceProviderManagement/Features/SalesPoint/Hooks'
import { useDeleteSingleSalesPointMutation } from 'Services/ServiceProviderManagement/Features/SalesPoint/salesPointApi'
import {
  ROWS_PER_PAGE_DEFAULT,
  SERVICE_PROVIDER_MANAGEMENT_ROOT_URL,
  SORT_ORDER_ASC,
  SORT_ORDER_DESC
} from 'Util/constants'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import { Box, Typography } from '@mui/material'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

export const SALES_POINT_DETAILS_DRAWER_TAB_PARAMETER_KEY = 'salesPointDetailsDrawerTab'

export default function SalesPointListPage() {
  const baseUrl = `${SERVICE_PROVIDER_MANAGEMENT_ROOT_URL}/sales-points`
  const navigate = useNavigate()
  const {
    salesPoints,
    isFetching: isSalesPointsFetching,
    totalResults,
    params,
    setParams,
    selectedSalesPointId,
    setSelectedSalesPointId
  } = useSalesPointsWithParams()
  const [searchText, setSearchText] = useState(params.name ?? '')
  const [searchParams, setSearchParams] = useSearchParams()

  const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedSalesPointId)

  const [deleteSalesPoint, { isLoading: isDeleteLoading }] = useDeleteSingleSalesPointMutation()

  const breadcrumbs = [
    {
      title: t`Home`,
      link: SERVICE_PROVIDER_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Sales points`
    }
  ]

  function handleRequestSort(_event: React.MouseEvent<unknown, MouseEvent>, property: string) {
    const isAsc = params.sort_order === SORT_ORDER_ASC
    setParams(prevState => ({
      ...prevState,
      sort_by: property,
      sort_order: `${isAsc ? SORT_ORDER_DESC : SORT_ORDER_ASC}`
    }))
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setParams(prevState => ({
      ...prevState,
      start_index: (newPage * ROWS_PER_PAGE_DEFAULT).toString()
    }))
  }

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const handleSearch = () => {
    setParams(prevState => ({
      ...prevState,
      search_text: searchText,
      start_index: undefined
    }))
  }

  const handleDrawerClose = () => {
    setSelectedSalesPointId('')
    setIsDrawerOpen(false)
    searchParams.delete(SALES_POINT_DETAILS_DRAWER_TAB_PARAMETER_KEY)
    setSearchParams(searchParams)
  }

  const handleRowClick = (id: string) => {
    setSelectedSalesPointId(id)
    setIsDrawerOpen(true)
  }

  const handleDelete = () => {
    if (!selectedSalesPointId) return
    deleteSalesPoint(selectedSalesPointId)
      .unwrap()
      .then(() => {
        setParams(params)
        handleDrawerClose()
        SnackbarUtils.success(t`Sales point successfully deleted.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`An error occurred while deleting the sales point.`)
      })
  }

  const isLoading = isSalesPointsFetching || isDeleteLoading

  return (
    <Box sx={{ width: '100%' }}>
      <Breadcrumbs items={breadcrumbs} />
      <Box sx={{ mb: 2 }}>
        <Typography variant="h1" sx={{ mb: 2 }}>{t`Sales points`}</Typography>
        <Box mt={4}>
          <ListActions>
            <CustomButton
              onClick={() => navigate(`${baseUrl}/create/`)}
              size="small"
              startIcon={<AddIcon />}
            >
              {t`Create new sales point`}
            </CustomButton>
            <ListSearch
              searchText={searchText}
              handleSearchTextChange={handleSearchTextChange}
              handleSearch={handleSearch}
              placeholder={t`Search by name or code`}
            />
          </ListActions>
          <Box pt={1}>
            <WithLoader loading={isLoading} rows={1} height={33}>
              <SalesPointList
                rows={salesPoints}
                isLoading={isLoading}
                params={params}
                handleRequestSort={handleRequestSort}
                handleChangePage={handleChangePage}
                count={totalResults}
                page={
                  params.start_index
                    ? Number.parseInt(params.start_index, 10) / ROWS_PER_PAGE_DEFAULT
                    : 0
                }
                onRowClick={handleRowClick}
                selectedSalesPointId={selectedSalesPointId}
              />
            </WithLoader>
          </Box>
        </Box>
        <SalesPointDrawer
          open={isDrawerOpen}
          handleClose={handleDrawerClose}
          handleSalesPointDelete={handleDelete}
          selectedSalesPointId={selectedSalesPointId}
        />
      </Box>
    </Box>
  )
}
